import React, { useState } from 'react';
import { SPM_FIGURE_METADATA } from '../data/ipccData';
import { OriginalIPCCFigureDiagram } from './OriginalIPCCFigureDiagram';
import { DynamicSonificationDashboard } from './DynamicSonificationDashboard';
import { Eye, ExternalLink, ShieldCheck, FileText, BookOpen } from 'lucide-react';

interface SymphonyConductorStageProps {
  activeMovementIdx: number;
  progress: number;
  isPlaying: boolean;
  onScrubProgress: (newProgress: number) => void;
}

const MOVEMENT_CONFIGS: Record<number, { figA: string; figB: string; title: string; subtitle: string }> = {
  0: { figA: 'spm1', figB: 'spm2', title: 'Movement I: The Human Signal', subtitle: 'SPM.1 Historical Warming & SPM.2 Human Drivers' },
  1: { figA: 'spm3', figB: 'spm9', title: 'Movement II: Regional Extremes & Risk Matrix', subtitle: 'SPM.3 Hex-Grid Extremes & SPM.9 Impact-Driver Matrix' },
  2: { figA: 'spm4', figB: 'spm5', title: 'Movement III: Future Scenarios & Spatial Warming', subtitle: 'SPM.4 Scenario Trajectories & SPM.5 Spatial Polar Amplification' },
  3: { figA: 'spm6', figB: 'spm7', title: 'Movement IV: Carbon Metronome & Extreme Accelerations', subtitle: 'SPM.6 Cumulative Carbon & SPM.7 Extreme Multipliers' },
  4: { figA: 'spm8', figB: 'spm10', title: 'Movement V: Multi-Century Commitment & Carbon Budget', subtitle: 'SPM.8 Sea Level Commitment & SPM.10 Carbon Allowance' }
};

export const SymphonyConductorStage: React.FC<SymphonyConductorStageProps> = ({
  activeMovementIdx,
  progress,
  isPlaying,
  onScrubProgress
}) => {
  const movConfig = MOVEMENT_CONFIGS[activeMovementIdx] ?? MOVEMENT_CONFIGS[0];
  const [selectedFigId, setSelectedFigId] = useState<string>(movConfig.figA);

  const activeFigId = selectedFigId === movConfig.figA || selectedFigId === movConfig.figB ? selectedFigId : movConfig.figA;
  const meta = SPM_FIGURE_METADATA[activeFigId];

  return (
    <div className="space-y-6">
      {/* 1. STAGE 1: Dedicated Real-Time Dynamic Sonification Dashboard (Top View) */}
      <DynamicSonificationDashboard
        activeMovementIdx={activeMovementIdx}
        figId={activeFigId}
        progress={progress}
        isPlaying={isPlaying}
        onScrubProgress={onScrubProgress}
      />

      {/* 2. STAGE 2: Pristine Original IPCC Reference Figure (Bottom Reference - 100% Clean, Zero Overlays) */}
      <div className="glass-panel p-6 lg:p-8 rounded-3xl border border-slate-800 space-y-4 shadow-xl bg-slate-950/80">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-bold text-emerald-400 flex items-center gap-1">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Pristine Publication Graphic (100% Uncluttered Reference)
              </span>
            </div>
            <h4 className="text-base font-bold text-white mt-0.5">
              Original IPCC SPM Figure Diagram: {meta?.number} &bull; {meta?.title}
            </h4>
          </div>

          {/* Reference Figure Selector Tabs & External IPCC Link */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-xl border border-slate-800">
              <button
                onClick={() => setSelectedFigId(movConfig.figA)}
                className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all ${
                  activeFigId === movConfig.figA
                    ? 'bg-cyan-500 text-slate-950 shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                View {SPM_FIGURE_METADATA[movConfig.figA]?.number} Graphic
              </button>
              <button
                onClick={() => setSelectedFigId(movConfig.figB)}
                className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all ${
                  activeFigId === movConfig.figB
                    ? 'bg-purple-500 text-slate-950 shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                View {SPM_FIGURE_METADATA[movConfig.figB]?.number} Graphic
              </button>
            </div>

            <a
              href={meta?.originalIpccUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-slate-900 hover:bg-slate-800 text-cyan-400 border border-slate-800 transition-colors"
            >
              <span>Official IPCC Link</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Pure Untouched Original Graphic (Zero Crosshairs, Zero Dots, Zero Lines) */}
        <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-950">
          <OriginalIPCCFigureDiagram figureId={activeFigId} originalIpccUrl={meta?.originalIpccUrl} />
        </div>

        {/* Scientific Takeaway & Policy Context */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1">
            <h5 className="text-xs font-bold text-cyan-300 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-cyan-400" />
              <span>IPCC SPM Key Scientific Takeaways</span>
            </h5>
            <ul className="text-xs text-slate-300 space-y-1 list-disc list-inside leading-relaxed">
              {meta?.keyTakeaways.slice(0, 3).map((takeaway, idx) => (
                <li key={idx}>{takeaway}</li>
              ))}
            </ul>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-1">
            <h5 className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-amber-400" />
              <span>Policy & Assessment Relevance</span>
            </h5>
            <p className="text-xs text-slate-300 leading-relaxed">
              {meta?.policyRelevance}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
